/* eslint-disable @typescript-eslint/no-explicit-any */
import { parse, differenceInDays, formatDistanceToNowStrict } from 'date-fns'

import {
  dateOnly,
  MetricConfigInterface,
  MetricInterface,
  MetricConfigColors,
} from '@/types/models'
import getNestedValue from '@/helpers/nestedValue'
import { sortStuff } from '@/helpers/sorters'
// import Harvest from "./Harvest";
import { isString } from '@/helpers/strings'
import { dateDiffWordsDays } from '@/helpers/dateHelpers'
import { capitalCase } from '@/helpers/capitalize'

export enum MetricType {
  value = 'value',
  firstOfArray = 'firstOfArray',
}

const toDate = (val: string | number | Date) => new Date(val)
const differenceInDaysToNow = (date: Date | number) =>
  differenceInDays(date, new Date().setHours(0, 0, 0, 0))

const DATE_ONLY_FORMAT = 'yyyy-MM-dd'
const FORMAT_DISTANCE_TO_NOW_OPTIONS = {
  addSuffix: true,
  unit: 'day' as 'day',
}

const parseDateOnly = (
  dateString: string,
  formatString: string = DATE_ONLY_FORMAT
) => parse(dateString, formatString, new Date())

interface Converters {
  formatDistanceToNowStrict: (date: Date | number, formatOptions: {}) => string
  differenceInDaysToNow: (date: Date | number) => number
  toDate: (val: string | number | Date) => Date
  parseDateOnly: (val: string) => Date
  // getNestedValue: ();
  dateDiffWordsDays: (val: number) => string
  capitalCase: (val: string) => string
}

interface ConverterObj {
  function: keyof Converters
  args: Array<any>
}

const converters = {
  formatDistanceToNowStrict: (
    date: Date | number,
    formatOptions = FORMAT_DISTANCE_TO_NOW_OPTIONS
  ) => formatDistanceToNowStrict(date, formatOptions),
  differenceInDaysToNow,
  toDate,
  parseDateOnly,
  getNestedValue,
  dateDiffWordsDays,
  capitalCase,
}

// const TEST_FORMAT = [
//   "parseDateOnly",
//   ["formatDistanceToNowStrict", { addSuffix: true, unit: "day" }]
// ];

const evalConverter = (
  value: string | number | Date | dateOnly,
  converter: keyof Converters | [keyof Converters, ...any[]] | ConverterObj
) =>
  Array.isArray(converter)
    ? (converters[converter[0]] as (val: any, ...args: any) => any)(
        value,
        ...converter.slice(1)
      )
    : isString(converter)
    ? (converters[converter as keyof Converters] as (val: any) => any)(value)
    : (
        converters[(converter as ConverterObj).function] as (
          val: any,
          ...args: any
        ) => any
      )(value, ...(converter as ConverterObj).args)
const evalConverters = <T>(
  value: string | number | Date | dateOnly | T,
  converters: Array<any>
) => converters.reduce((val, converter) => evalConverter(val, converter), value)

// interface MetricTypesInterface {
//   [key: MetricType]: MetricTypeInterface
// }

// interface MetricTypeInterface<T, ConfigT> {
//   getter: (item: T, config: ConfigT) => object | number | string;
//   value?: (item: T, config: ConfigT) => number | string;
// }

interface FilterInterface<T> {
  [key: string]: any
}
const DEFAULT_FILTER_INPUT = {} // (item: any) => true
// const DEFAULT_VALUE = <T>(value: T) => value;
// const DEFAULT_FORMAT = <T>(value: T) => value;

const filterThing =
  <T>(filter: FilterInterface<T>) =>
  (item: T): boolean =>
    Object.keys(filter).every(
      (key: string) => item[key as keyof T] === filter[key]
    )

type GetterReturnType = any // string | number | object

const metricTypes = {
  [MetricType.value]: {
    getter: <T>(item: T, config: MetricConfigInterface<T>) =>
      getNestedValue(item, config.field as string) as GetterReturnType,
    // valueFn: DEFAULT_VALUE,
    // formatFn: DEFAULT_FORMAT
  },
  [MetricType.firstOfArray]: {
    getter: <T>(item: T, config: MetricConfigInterface<T>) =>
      (getNestedValue(item, config.field as string) as Array<any>)
        .filter(filterThing(config.filter || DEFAULT_FILTER_INPUT))
        .sort(
          sortStuff(...(config.sort ? config.sort : []))
        )[0] as GetterReturnType,
    // valueFn: (value: Harvest) => value.date,
    // formatFn: (value: dateOnly) => evalConverters(value, TEST_FORMAT)
    // formatFn: (value: dateOnly) => formatDistanceToNowStrict(parse(value, 'yyyy-MM-dd', 0), { addSuffix: true, unit: 'day' }),
    // formatFn: (value: dateOnly) => formatDistanceToNowStrict(parse(value, 'yyyy-MM-dd', new Date()), { addSuffix: true, unit: 'day' }),
    // formatFn: (value: dateOnly) => Function.call(this, 'formatDistanceToNowStrict', parse(value, 'yyyy-MM-dd', new Date()), { addSuffix: true, unit: 'day' }), // differenceInDays(parse(value, 'yyyy-MM-dd', new Date()), new Date().setHours(0, 0, 0, 0))
  },
}

const evalColor = (
  value: number | string | Date,
  colorConfig: MetricConfigColors
) => {
  const op = colorConfig.op || '=='
  const { value: compareValue, color: thisColor } = colorConfig

  switch (op) {
    case '=':
    case '==':
      return value == compareValue ? thisColor : undefined
    case '!=':
      return value != compareValue ? thisColor : undefined
    case '<':
      return value < compareValue ? thisColor : undefined
    case '<=':
      return value <= compareValue ? thisColor : undefined
    case '>':
      return value > compareValue ? thisColor : undefined
    case '>=':
      return value >= compareValue ? thisColor : undefined
    default:
      return undefined
  }
}
const evalColors = (
  value: number | string | Date,
  colors: Array<MetricConfigColors>
) =>
  colors.reduce(
    (color: string | undefined, colorConfig) =>
      color || evalColor(value, colorConfig),
    undefined
  )

const DEFAULT_ICON = 'mdi-pound'

export default class Metric<ParentT> {
  type: MetricType
  name: string
  icon: string
  config: MetricConfigInterface<ParentT>
  // parent: ParentT
  data?: ParentT = undefined

  constructor(input: MetricInterface<ParentT>) {
    this.type = input.type
    this.name = input.name
    this.icon = input.icon || DEFAULT_ICON
    this.config = input.config
  }

  get metricTypeConfig() {
    return metricTypes[this.type]
  }

  get getter() {
    return this.metricTypeConfig.getter
  }

  get valueConverters() {
    return this.config.valueConverters || [] // this.metricTypeConfig.valueFn; // ? this.metricTypeConfig.value : DEFAULT_VALUE
  }

  get formatConverters() {
    return this.config.formatConverters || []
  }

  get labelConverters() {
    return this.config.labelConverters || []
  }

  get iconConverters() {
    return this.config.iconConverters || []
  }

  getValue(data: ParentT) {
    return evalConverters(this.getter(data, this.config), this.valueConverters)
    // return this.formatFn(this.valueFn(this.getter(data, this.config)));
  }

  getFormattedValue(data: ParentT) {
    return evalConverters(this.getValue(data), this.formatConverters)
    // return this.formatFn(this.valueFn(this.getter(data, this.config)));
  }

  getLabel(data: ParentT) {
    return this.labelConverters.length > 0
      ? evalConverters(this.getter(data, this.config), this.labelConverters)
      : this.name
  }

  getIcon(data: ParentT) {
    return this.iconConverters.length > 0
      ? evalConverters(this.getter(data, this.config), this.iconConverters)
      : this.icon
  }

  getColor(data: ParentT) {
    return evalColors(this.getValue(data), this.config.colors || [])
  }

  static fromFirestore<ParentT>(
    data: MetricInterface<ParentT>
  ): Metric<ParentT> {
    const result: MetricInterface<ParentT> = {
      type: data.type,
      name: data.name,
      icon: data.icon,
      config: data.config,
    }
    return new Metric<ParentT>(result)
  }
}
